"use client"

import { useEffect, useState } from "react"
import { CheckCheck } from "lucide-react"
import { Button } from "@/components/ui/animated/button/button"
import { useI18n } from "@/i18n/client"
import { communicationActions as actions } from "./state"
import { useCommunications } from "./use-communications"

type Props = {
  target: "threads" | "notifications"
  filters?: Record<string, string>
  disabled?: boolean
  onMarked?: () => void | Promise<void>
  className?: string
}

export function MarkAllReadButton(props: Props) {
  const state = useCommunications()
  return <ReadAllAction key={JSON.stringify([state.viewer, state.cacheScope, props.target, props.filters])} {...props} disabled={props.disabled || state.status !== "ready"} />
}

function ReadAllAction({ target, filters, disabled, onMarked, className }: Props) {
  const { t } = useI18n()
  const [pending, setPending] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")
  useEffect(() => { if (!feedback) return; const timer = setTimeout(() => setFeedback(""), 2000); return () => clearTimeout(timer) }, [feedback])
  async function mark() {
    if (pending) return
    setPending(true); setError(""); setFeedback("")
    let saved = false
    try {
      await actions.readAll(target, filters)
      saved = true
      await onMarked?.()
      setFeedback(t("Marked as read."))
    } catch (issue) {
      if (issue instanceof DOMException && issue.name === "AbortError") return
      setError(saved ? t("Read state saved. Refresh this view to see the latest messages.") : issue instanceof Error ? issue.message : t("Could not mark items as read. Try again."))
    } finally { setPending(false) }
  }
  return <div className={className}>
    <Button type="button" size="sm" variant="ghost" className="min-h-11 whitespace-nowrap" disabled={disabled || pending} loading={pending} aria-busy={pending} onClick={mark}><CheckCheck data-icon="inline-start" />{feedback ? t("Marked read") : t("Mark all read")}</Button>
    <span className="sr-only" role="status">{feedback}</span>
    {error && <p className="max-w-64 text-xs text-destructive" role="alert">{error}</p>}
  </div>
}
